import { Box } from "@mui/material";
import { toPng } from "html-to-image";
import PptxGenJS from "pptxgenjs";
import { useEffect, useRef, useState, useMemo } from "react";
import { Building } from "../../db_access/building";
import GeneratePPTButton from "../button/GeneratePPTButton";
import { useContextProvider } from "../page-context/ContextProvider";
// import { addDescPages, addReferences, addInsights } from "./ExportProductPPT"; // Removed
import {
    addCumulativeProjection,
    addImpactOverview,
    addDetailedBreakdown,
    addMaterialHotspotsChart,
    addHotspots,
    addBuildingCoverPage,
    addDescPages, // Added
    addInsights, // Added
    addReferences // Added
} from "./ExportBuildingPPT";
import { scaleOrdinal } from "d3-scale";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, Cell, LabelList, PieChart, Pie } from 'recharts';
import { Company } from "../../db_access/company";

// --- CONSTANTS & HELPERS (Copied from ResultTab.js) ---
const COLORS = {
    Embodied: "#ef4444", Operational: "#f59e0b", Biogenic: "#10b981", Product: "#3b82f6", Gray: "#64748b"
};
const SECTION_COLORS = {
    "Substructure": "#0ea5e9", "substructure": "#0ea5e9", "Interiors": "#d946ef", "interiors": "#d946ef",
    "Shell": "#14b8a6", "shell": "#14b8a6", "Services": "#f97316", "services": "#f97316", "Other": "#94a3b8"
};

const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    const absNum = Math.abs(num);
    if (absNum >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (absNum >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
    }
    return Math.round(num).toString();
};

const toTons = (kg) => (kg / 1000);
const formatNumberNoDecimals = (num) => Math.round(num).toLocaleString();
const formatToTons = (kg) => (kg / 1000).toFixed(1);

const getScreenshot = async (ref) => {
    if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff", pixelRatio: 3 });
    }
    return null;
};

const generatePpt = async (data) => {
    const pptx = new PptxGenJS();
    addBuildingCoverPage(pptx, data);

    let num = 1;
    num = addDescPages(pptx, data, num);
    num = await addImpactOverview(pptx, data, num);
    num = await addCumulativeProjection(pptx, data, num);
    num = await addDetailedBreakdown(pptx, data, num);
    num = await addMaterialHotspotsChart(pptx, data, num); // Chart
    num = addHotspots(pptx, data, num); // Table
    num = addInsights(pptx, data, num);
    num = addReferences(pptx, data, num);

    pptx.writeFile({ fileName: `Building_Report_${data.buildingName || "Template"}.pptx` });
};

const ExportBuildingPPTButton = () => {
    const { selectedBuildings, enqueueAlert } = useContextProvider();
    const [data, setData] = useState(null);
    const [company, setCompany] = useState();
    const [ai_insight, setAI_insight] = useState();
    const [isGenerating, setIsGenerating] = useState(false);

    // Refs for screenshots
    const cumulativeRef = useRef();
    const lcaStagesRef = useRef();
    const impactRatioRef = useRef();
    const sectionsRef = useRef();
    const hotspotsRef = useRef();
    const sectionsTableRef = useRef();

    useEffect(() => {
        Company.get_mine()
            .then((res) => res.json())
            .then((data) => setCompany(data.length > 0 ? data[0] : null));
    }, []);

    useEffect(() => {
        if (selectedBuildings.length === 1) {
            const buildingId = selectedBuildings[0].id;
            const payload = {
                building_id: buildingId,
                filter_category: "Global",
                filter_subcategory: null
            };

            Building.get_results(payload)
                .then((res) => res.json())
                .then((res) => setData(res))
                .catch((err) => console.error("Fetch Error:", err));

            Building.get_insight(buildingId)
                .then((res) => res.json())
                .then((data) => setAI_insight(data))
                .catch((err) => console.error("Fetch Insight Error:", err));
        } else {
            setData(null);
            setAI_insight(null);
        }
    }, [selectedBuildings]);

    // --- DATA PROCESSING (Copied from ResultTab.js) ---
    const building = selectedBuildings[0];
    const serviceLife = building?.service_life || 60; // Default if missing
    // const extendedLife = building?.extended_service_life || 0;
    // const maxDuration = serviceLife + extendedLife;
    const duration = serviceLife; // Use service life for PPT projection

    const embodied = data?.embodiedCarbon ?? data?.metrics?.embodiedCarbon ?? 0;
    const operational = data?.operationalCarbon ?? data?.metrics?.operationalCarbon ?? 0;
    // const biogenic = data?.biogenicCarbon ?? data?.metrics?.biogenicCarbon ?? 0;
    const totalCarbon = embodied + operational;

    // Projection Data
    const projectionData = useMemo(() => {
        const startYear = building?.Year || building?.year || 2025;
        const embodiedVal = embodied || 0;
        const operationalVal = operational || 0;

        const proj = [];
        for (let i = 0; i <= duration; i += 5) { // Step by 5 years for cleaner chart in PPT? Or just every year. Let's do every 5 to save rendering time if needed, but every year is fine.
            const currentYear = startYear + i;
            const cumulativeOp = i * operationalVal;
            proj.push({
                year: currentYear,
                embodied: embodiedVal,
                operational: cumulativeOp,
                total: embodiedVal + cumulativeOp
            });
        }
        return proj;
    }, [embodied, operational, duration, building]);

    // LCA Data
    const lcaData = useMemo(() => {
        if (!data) return [];
        const stages = data.lcaGroups || data.lcaStages || [];
        const chartMap = Object.fromEntries(stages.map(i => [i.stage_group, i]));
        const total = stages.reduce((acc, curr) => acc + Math.abs(curr.value), 0);

        return [
            { key: "A1-A5", label: "A1-A5", color: "#3b82f6" },
            { key: "B1-B5", label: "B1-B5", color: "#10b981" },
            { key: "B6-B7", label: "B6-B7", color: "#f59e0b" },
            { key: "C1-C4", label: "C1-C4", color: "#ef4444" },
            { key: "D", label: "D", color: "#8b5cf6" }
        ].map(c => {
            const val = chartMap[c.key]?.value ?? 0;
            const absVal = Math.abs(val);
            const pct = total > 0 ? Math.round((absVal / total) * 100) : 0;
            return {
                stage: c.label,
                name: c.label,
                value: val,
                absValue: absVal,
                color: c.color,
                pct: Math.min(pct, 100)
            };
        });
    }, [data]);

    const lcaPieData = lcaData.filter(d => Math.abs(d.value) > 1).map(d => ({
        ...d,
        value: Math.abs(d.value)
    }));

    // Donut Data (Sections)
    const donutData = useMemo(() => data?.buildingSections?.map((s) => ({
        name: capitalize(s.category),
        value: Math.abs(s.subs.reduce((a, b) => a + b.impact, 0)),
        color: SECTION_COLORS[s.category] || COLORS.Gray
    })) || [], [data]);

    // Hotspots Data
    const hotspotData = useMemo(() => {
        if (!data || !data.materials) return [];
        const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
        const colorScale = scaleOrdinal(colors);
        return data.materials.slice(0, 10).map((m, i) => {
            const materialName = m.material || "Unknown Material";
            return {
                name: materialName.length > 25 ? materialName.substring(0, 25) + '...' : materialName,
                fullName: materialName,
                impact: m.impact,
                pct: m.pct,
                fill: colorScale(i)
            };
        });
    }, [data]);

    const top3SectionsData = useMemo(() => {
        if (!data?.buildingSections) return [];
        const targetCategories = ["Substructure", "Shell", "Interiors"];
        return targetCategories.map(cat => {
            const section = data.buildingSections.find(s => s.category.toLowerCase() === cat.toLowerCase());
            if (!section) return null;
            const totalImpact = section.subs.reduce((a, b) => a + b.impact, 0);
            const top3 = [...section.subs].sort((a, b) => b.impact - a.impact).slice(0, 3);
            return {
                category: cat,
                total: totalImpact,
                items: top3.map(item => ({
                    name: item.name,
                    impact: item.impact,
                    pct: totalImpact > 0 ? (item.impact / totalImpact) * 100 : 0
                }))
            };
        }).filter(Boolean);
    }, [data]);

    const handleGenerate = () => {
        if (!data || !building || isGenerating) return;
        setIsGenerating(true);
    };

    useEffect(() => {
        if (!isGenerating) return;

        let isMounted = true;
        const captureAndExport = async () => {
            enqueueAlert?.({ message: "Generating report, please wait...", severity: "info" });
            try {
                // Allow lazy DOM container to mount and charts to render
                await new Promise((resolve) => setTimeout(resolve, 600));
                if (!isMounted) return;

                const cumulativeProjectionChart = await getScreenshot(cumulativeRef);
                const lcaStagesChart = await getScreenshot(lcaStagesRef);
                const impactRatioChart = await getScreenshot(impactRatioRef);
                const sectionsChart = await getScreenshot(sectionsRef);
                const sectionsTable = await getScreenshot(sectionsTableRef);
                const hotspotsChart = await getScreenshot(hotspotsRef);

                const pptData = {
                    company,
                    buildingName: building.name,
                    goal: `To evaluate the environmental impacts of ${building.name}.`,
                    scope: `Cradle-to-Grave assessment over ${serviceLife} years service life.`,
                    functional_unit: `1 m² of ${building.building_type || "Building"}`,
                    system_boundary: "A1-A5, B1-B7, C1-C4, D",
                    footprint: formatToTons(totalCarbon),
                    assumptions: "Standard assumptions apply.",
                    cumulativeProjectionChart,
                    lcaStagesChart,
                    impactRatioChart,
                    sectionsChart,
                    sectionsTable,
                    hotspotsChart,
                    insights: ai_insight?.insights,
                    env_hotspot: ai_insight?.env_hotspot,
                    lca_info: ai_insight?.lca_info
                };

                await generatePpt(pptData);
                if (isMounted) {
                    enqueueAlert?.({ message: "Building report downloaded successfully!", severity: "success" });
                }
            } catch (err) {
                console.error("Error generating PPT:", err);
                if (isMounted) {
                    enqueueAlert?.({ message: "Failed to generate report. Please try again.", severity: "error" });
                }
            } finally {
                if (isMounted) {
                    setIsGenerating(false);
                }
            }
        };

        captureAndExport();

        return () => {
            isMounted = false;
        };
    }, [isGenerating]);

    return (
        <Box>
            {isGenerating && (
                <Box sx={{ position: "absolute", left: "-9999px", top: 0 }}>
                {/* Cumulative Projection */}
                <div ref={cumulativeRef} style={{ width: 1000, height: 600, padding: 40, backgroundColor: 'white', display: 'flex', flexDirection: 'column', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 20, marginTop: 0 }}>Cumulative Carbon Projection</h4>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 30, marginBottom: 20, fontSize: 16 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: COLORS.Embodied }}></span> Embodied: <b>{formatToTons(embodied)}</b> Tons CO₂e</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: COLORS.Operational }}></span> Operational: <b>{formatToTons(operational * duration)}</b> Tons CO₂e</span>
                    </div>
                    <ResponsiveContainer width="100%" height={480}>
                        <AreaChart data={projectionData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                            <defs>
                                <linearGradient id="colorEmbodied" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLORS.Embodied} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={COLORS.Embodied} stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="colorOperational" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLORS.Operational} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={COLORS.Operational} stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="year" tick={{ fontSize: 14 }} label={{ value: 'Year', position: 'insideBottom', offset: -10, fontSize: 14 }} />
                            <YAxis tickFormatter={(v) => toTons(v).toFixed(0)} tick={{ fontSize: 14 }} label={{ value: 'Tons CO₂e', angle: -90, position: 'insideLeft', fontSize: 14 }} />
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <Area type="monotone" dataKey="embodied" stackId="1" stroke={COLORS.Embodied} fill="url(#colorEmbodied)" />
                            <Area type="monotone" dataKey="operational" stackId="1" stroke={COLORS.Operational} fill="url(#colorOperational)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* LCA Stages Bar Chart */}
                <div ref={lcaStagesRef} style={{ width: 800, height: 600, padding: 30, backgroundColor: 'white', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 30, marginTop: 0 }}>LCA Stages</h4>
                    <ResponsiveContainer width="100%" height={480}>
                        <BarChart data={lcaData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="stage" tick={{ fontSize: 14 }} axisLine={{ stroke: '#333' }} tickLine={{ stroke: '#333' }} label={{ value: 'Life Cycle Stages', position: 'insideBottom', offset: -20, fontSize: 14 }} />
                            <YAxis tick={{ fontSize: 14 }} axisLine={{ stroke: '#333' }} tickLine={{ stroke: '#333' }} tickFormatter={(v) => formatToTons(v)} label={{ value: 'Tons CO₂e', angle: -90, position: 'insideLeft', fontSize: 14 }} />
                            <Bar dataKey="value" barSize={60} radius={[4, 4, 0, 0]}>
                                {lcaData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                <LabelList dataKey="value" position="top" formatter={formatNumber} style={{ fontSize: 14, fontWeight: 'bold' }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Impact Ratio Pie Chart */}
                <div ref={impactRatioRef} style={{ width: 800, height: 600, padding: 30, backgroundColor: 'white', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 20, marginTop: 0 }}>Impact Ratio</h4>
                    <ResponsiveContainer width="100%" height={450}>
                        <PieChart>
                            <Pie data={lcaPieData} dataKey="value" nameKey="name" innerRadius={120} outerRadius={180} paddingAngle={2} label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                                {lcaPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', marginTop: 20 }}>
                        {lcaPieData.map((e, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
                                <div style={{ width: 16, height: 16, backgroundColor: e.color, borderRadius: '50%' }}></div>
                                <span>{e.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sections Pie Chart */}
                <div ref={sectionsRef} style={{ width: 1000, height: 600, padding: 30, backgroundColor: 'white', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 30, marginTop: 0 }}>Building Sections Split</h4>
                    <ResponsiveContainer width="100%" height={450}>
                        <PieChart>
                            <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={120} outerRadius={180} paddingAngle={2} label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                                {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', marginTop: 20 }}>
                        {donutData.map((e, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
                                <div style={{ width: 16, height: 16, backgroundColor: e.color, borderRadius: '50%' }}></div>
                                <span>{e.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top 3 Sections Table */}
                <div ref={sectionsTableRef} style={{ width: 800, padding: 40, backgroundColor: 'white', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 30, marginTop: 0 }}>Top Contributors by Section</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
                        {top3SectionsData.map((section, idx) => (
                            <div key={idx}>
                                <h5 style={{ margin: '0 0 10px 0', color: SECTION_COLORS[section.category] || '#333', fontSize: '18px', borderBottom: `3px solid ${SECTION_COLORS[section.category] || '#333'}`, paddingBottom: 6 }}>{section.category}</h5>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#5F7434', color: 'white' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 15px', fontWeight: '600', borderTopLeftRadius: 6 }}>Component</th>
                                            <th style={{ textAlign: 'right', padding: '12px 15px', fontWeight: '600' }}>Carbon (Tons CO₂e)</th>
                                            <th style={{ textAlign: 'right', padding: '12px 15px', fontWeight: '600', borderTopRightRadius: 6 }}>Contrib. (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {section.items.map((item, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <td style={{ padding: '12px 15px', color: '#374151' }}>{item.name}</td>
                                                <td style={{ textAlign: 'right', padding: '12px 15px', color: '#111827', fontWeight: '500' }}>{formatToTons(item.impact)}</td>
                                                <td style={{ textAlign: 'right', padding: '12px 15px', color: '#4b5563' }}>{item.pct.toFixed(1)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Hotspots Bar Chart */}
                <div ref={hotspotsRef} style={{ width: 1000, height: 600, padding: 30, backgroundColor: 'white', fontFamily: 'Poppins, sans-serif' }}>
                    <h4 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: 30, marginTop: 0 }}>Material Hotspots</h4>
                    <ResponsiveContainer width="100%" height={480}>
                        <BarChart layout="vertical" data={hotspotData} margin={{ top: 20, right: 80, left: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                            <XAxis type="number" tick={{ fontSize: 14 }} label={{ value: 'Impact (kgCO₂e)', position: 'insideBottom', offset: -10, fontSize: 14 }} />
                            {/* <XAxis type="number" tick={{ fontSize: 14 }} tickFormatter={(v) => formatToTons(v)} label={{ value: 'Impact (Tons CO₂e)', position: 'insideBottom', offset: -10, fontSize: 14 }} /> */}
                            <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 12 }} />
                            <Bar dataKey="impact" barSize={30} radius={[0, 4, 4, 0]}>
                                {hotspotData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                                <LabelList dataKey="impact" position="right" formatter={(val) => val.toLocaleString()} style={{ fontSize: 12, fontWeight: 'bold' }} />
                                {/* <LabelList dataKey="impact" position="right" formatter={(val) => formatToTons(val)} style={{ fontSize: 12, fontWeight: 'bold' }} /> */}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>

                </div>

            </Box>
            )}
            <GeneratePPTButton onClick={handleGenerate} disabled={isGenerating} />
        </Box>
    );
};

export default ExportBuildingPPTButton;
